import { EditorProvider, useCurrentEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { cn } from "@/utils/cn";
import { Markdown } from "tiptap-markdown";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { List, ListOrdered } from "lucide-react";
import Section from "./Section";
import InsertSectionDropdown from "./InsertSectionDropdown";

import "@/styles/_keyframe-animations.scss";
import "@/styles/_variables.scss";

const Tiptap: React.FC<{
  content?: JSON | string;
  setEditorContent?: React.Dispatch<React.SetStateAction<JSON | string>>;
  onEditorReady?: (editor: any) => void;
  initialiseAsEditable?: boolean;
  lyricSectionKinds?: string[];
}> = ({
  content,
  setEditorContent,
  onEditorReady,
  initialiseAsEditable = false,
  lyricSectionKinds,
}) => {
  const handleUpdate = ({ editor }: { editor: any }) => {
    if (setEditorContent) {
      setEditorContent(editor.getJSON());
    }
  };

  const MenuButton: React.FC<{
    isActive: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    children: React.ReactNode;
  }> = ({ isActive, onClick, children }) => {
    return (
      <button
        className={cn(
          "bg-cream-400 dark:bg-cream-800 flex flex-row items-center gap-1 rounded-lg px-3 py-1 text-sm font-medium shadow hover:cursor-pointer hover:shadow-lg lg:text-base",
          isActive && "bg-cream-800 text-cream-100 dark:bg-cream-700",
        )}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
      >
        {children}
      </button>
    );
  };

  const MenuBar = () => {
    const { editor } = useCurrentEditor();
    if (!editor) {
      return null;
    }

    if (!editor.isEditable) {
      return null;
    }

    if (lyricSectionKinds) {
      return (
        <div className="flex flex-row flex-wrap gap-2 pt-10">
          <InsertSectionDropdown editor={editor} kinds={lyricSectionKinds} />
        </div>
      );
    }

    return (
      <div className="flex flex-row flex-wrap gap-2 pt-10">
        <MenuButton
          isActive={editor.isActive("heading", { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          Heading
        </MenuButton>
        <MenuButton
          isActive={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Bold
        </MenuButton>
        <MenuButton
          isActive={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italic
        </MenuButton>
        <MenuButton
          isActive={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List strokeWidth={1.5} />
        </MenuButton>
        <MenuButton
          isActive={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered strokeWidth={1.5} />
        </MenuButton>
        <MenuButton
          isActive={false}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          Line
        </MenuButton>
      </div>
    );
  };

  return (
    <div
      className={
        initialiseAsEditable
          ? "dark:bg-cream-950 w-full rounded-2xl bg-white px-5 shadow lg:px-10"
          : "md:dark:bg-cream-950 w-full rounded-2xl bg-none md:bg-white md:px-5 md:shadow lg:px-10"
      }
    >
      <EditorProvider
        extensions={[
          StarterKit,
          Markdown,
          HorizontalRule,
          ...(lyricSectionKinds ? [Section] : []),
        ]}
        content={content}
        onCreate={({ editor }) => {
          if (onEditorReady) {
            onEditorReady(editor);
          }
        }}
        onUpdate={handleUpdate}
        editorContainerProps={{
          className:
            "rounded-xl prose prose-sm sm:prose-base lg:prose border-none w-full dark:prose-invert",
        }}
        editable={initialiseAsEditable}
        slotBefore={<MenuBar />}
      ></EditorProvider>
    </div>
  );
};

export default Tiptap;
